var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { styled, css } from '../theme-provider';
import { CharacterCounter } from '../character-counter';
import { getAuxillaryColor, getAuxillaryTextColor, getMainColor, getLabelColor, } from '../helpers/input';
import { getCommonProps } from '../helpers/common-props';
import { InputNotification } from '../helpers/input-notification';
const StyledLabelText = styled.span.withConfig({ displayName: "StyledLabelText", componentId: "sc-oyarl6" }) `font-size:${({ theme, isFloating }) => isFloating
    ? theme.fonts.copy[200].fontSize
    : theme.fonts.label[100].fontSize};width:${(props) => `calc(100% - ${props.theme.size.static250})`};white-space:nowrap;text-overflow:ellipsis;overflow:hidden;display:block;pointer-events:none;position:absolute;margin:0;padding:0 0 ${({ theme }) => parseInt(theme.size.static100) / 2}px 0;background-color:${({ theme }) => theme.colors.background.primary};top:${({ theme }) => theme.size.static200};transition:font-size 0.15s ease-in-out;${({ appearance }) => appearance === 'readonly'
    ? css `
					background-color: ${({ theme }) => theme.colors.background.tertiary};
				`
    : css `
					background-color: ${({ theme }) => theme.colors.background.primary};
				`} ${({ hasValue, theme }) => hasValue &&
    css `
			font-size: ${theme.fonts.label[100].fontSize};
			color: ${({ theme }) => theme.colors.content.tertiary};
		`}`;
const StyledTextArea = styled.textarea.withConfig({ displayName: "StyledTextArea", componentId: "sc-119nu2n" }) `width:100%;border-radius:0;min-height:${({ theme }) => `calc(${theme.size.static250} * 2 + ${theme.fonts.copy[200].lineHeight})`};background:transparent;border:0;color:${getMainColor};cursor:${({ appearance }) => (appearance === 'disabled' ? 'not-allowed' : '')};padding:${({ theme }) => theme.size.static250} ${({ theme }) => theme.size.static250} ${({ theme }) => theme.size.static200} 0;resize:vertical;outline:0;font-size:${({ theme }) => theme.fonts.copy[200].fontSize};line-height:${({ theme }) => theme.fonts.copy[200].lineHeight};font-family:inherit;transition:padding 0.15s ease-in-out;&::placeholder{opacity:1;color:${({ theme }) => theme.colors.content.secondary};}`;
const StyledLabel = styled.label.withConfig({ displayName: "StyledLabel", componentId: "sc-juh4hi" }) `display:block;width:100%;position:relative;`;
const StyledLabelInner = styled.div.withConfig({ displayName: "StyledLabelInner", componentId: "sc-bt9qv6" }) `display:block;width:100%;position:relative;padding-top:${({ theme }) => theme.size.static200};padding-left:${({ theme }) => theme.size.static250};color:${getLabelColor};box-shadow:0 0 0 ${({ theme }) => theme.size.border.light} ${getAuxillaryColor};border-radius:${({ theme }) => theme.size.static150};:focus-within{padding-bottom:${({ theme }) => theme.size.static0};}${({ appearance }) => appearance !== 'disabled' &&
    appearance !== 'readonly' &&
    css `
			&:focus-within ${StyledLabelText} {
				color: ${({ theme }) => theme.colors.content.tertiary};
				font-size: ${({ theme }) => theme.fonts.label[100].fontSize};
			}
		`} ${({ appearance }) => appearance === 'readonly' &&
    css `
			background-color: ${({ theme }) => theme.colors.background.tertiary};
		`} ${({ appearance }) => (appearance === 'success' ||
    appearance === 'error' ||
    appearance === 'information') &&
    css `
			box-shadow: 0 0 0 ${({ theme }) => theme.size.border.heavy}
				${getAuxillaryColor};
		`} ${({ appearance }) => (appearance === 'default' || appearance === 'validation') &&
    css `
			/* padding-bottom is enabled here and disabled on focus to prevent the block from changing height because of the border-width change */
			padding-bottom: ${({ theme }) => parseInt(theme.size.static100) / 2}px; // we don't have a static050 token, add and replace later if needed
			&:hover {
				transition: box-shadow 300ms ease;
				-webkit-transition: box-shadow 300ms ease;
				box-shadow: 0 0 0 ${({ theme }) => theme.size.border.light}
					${({ theme }) => theme.colors.border.hover};
			}
			&:focus-within {
				box-shadow: 0 0 0 ${({ theme }) => theme.size.border.heavy}
					${({ theme }) => theme.colors.focus.main};
			}
		`}`;
const StyledAdditionalInfo = styled.div.withConfig({ displayName: "StyledAdditionalInfo", componentId: "sc-61bvzt" }) `display:flex;flex-direction:row;justify-content:space-between;align-items:flex-start;gap:${({ theme }) => theme.size.static150};padding-top:${({ theme }) => theme.size.static100};width:100%;color:${getAuxillaryTextColor};`;
export const TextArea = (props) => {
    const labelRef = React.createRef();
    const { label, appearance = 'default', name, onBlur, onChange, onFocus, value, defaultValue, placeholder, rows = 1, minLength, maxLength, required, isFloating, message, inputMode, characterCountLimit = maxLength, customId } = props, rest = __rest(props, ["label", "appearance", "name", "onBlur", "onChange", "onFocus", "value", "defaultValue", "placeholder", "rows", "minLength", "maxLength", "required", "isFloating", "message", "inputMode", "characterCountLimit", "customId"]);
    const commonProps = getCommonProps(props);
    const [characterCount, setCharacterCount] = React.useState((value === null || value === void 0 ? void 0 : value.length) || (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.length) || 0);
    const [hasValue, setHasValue] = React.useState(false);
    const [labelHeight, setLabelHeight] = React.useState(0);
    const [isFocused, setIsFocused] = React.useState(false);
    React.useEffect(() => {
        setCharacterCount((value === null || value === void 0 ? void 0 : value.length) || 0);
    }, [value]);
    React.useEffect(() => {
        var _a;
        setLabelHeight(((_a = labelRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0);
    }, [labelRef]);
    const handleResize = React.useCallback(() => { var _a; return setLabelHeight(((_a = labelRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0); }, [setLabelHeight, labelRef]);
    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);
    const handleChange = React.useCallback((event) => {
        const currentValueLenght = event.currentTarget.value.length;
        setCharacterCount(currentValueLenght);
        setHasValue(currentValueLenght > 0);
        if (onChange) {
            onChange(event);
        }
    }, [onChange]);
    const handleFocus = (event) => {
        setIsFocused(true);
        if (onFocus) {
            onFocus(event);
        }
    };
    const isNotificationAppearance = (appearance) => ['error', 'success', 'information'].includes(appearance);
    /*
    ariaInvalid and ariaDescribedBy are used to set the aria-invalid and aria-describedby attributes
    according to the appearance of the input. These properties are needed to make the input accessible.
    */
    const ariaInvalid = appearance === 'error';
    const ariaDescribedBy = isNotificationAppearance(appearance)
        ? `${appearance}-message${customId}`
        : undefined;
    return (React.createElement(StyledLabel, Object.assign({ appearance: appearance }, commonProps, rest),
        React.createElement(StyledLabelInner, { appearance: appearance, hasValue: hasValue },
            label && (React.createElement(StyledLabelText, { "aria-hidden": true, appearance: appearance, ref: labelRef, isFloating: isFloating, hasValue: hasValue, labelHeight: labelHeight },
                label,
                required && '*')),
            React.createElement(StyledTextArea, { readOnly: appearance === 'readonly', disabled: appearance === 'disabled', "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid, rows: Math.max(2, rows), appearance: appearance, minLength: minLength, maxLength: maxLength, name: name, onChange: handleChange, onBlur: onBlur, onFocus: handleFocus, placeholder: placeholder, value: value, defaultValue: defaultValue, required: required, "aria-required": required, hasValue: characterCount > 0, inputMode: inputMode, labelHeight: labelHeight })),
        React.createElement(StyledAdditionalInfo, { appearance: appearance },
            message && isNotificationAppearance(appearance) && (React.createElement(InputNotification, { appearance: appearance, text: message, isFocused: isFocused, hideIcon: true, iconVariant: "default" })),
            characterCountLimit && (React.createElement(CharacterCounter, { value: characterCount, limit: characterCountLimit })))));
};
